import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useProSubscription } from '../contexts/ProSubscriptionContext';
import * as LinkFactory from '../routing/LinkFactory';
import { FaCrown, FaLock } from 'react-icons/fa';
import MainLayout from '../pages/layout/MainLayout';
import './ProRouteGuard.css';

interface ProRouteGuardProps {
    children: React.ReactNode;
}

/**
 * ProRouteGuard
 *
 * Wraps routes that require a Blift Pro subscription.
 * When the RCIC does not have Pro access, renders an upgrade prompt
 * instead of the protected content.
 *
 * While the Pro access status is loading, children are rendered normally
 * to avoid a flash of the upgrade prompt.
 */
const ProRouteGuard: React.FC<ProRouteGuardProps> = ({ children }) => {
    const { hasProAccess, isLoading } = useProSubscription();
    const navigate = useNavigate();

    // Render children while loading to avoid flash
    if (isLoading) return <>{children}</>;

    if (!hasProAccess) {
        return (
            <MainLayout>
                <div className="pro-upgrade-card">
                        <div className="pro-upgrade-glow" aria-hidden="true" />
                        <div className="pro-upgrade-icon">
                            <FaLock aria-hidden="true" />
                            <span className="pro-upgrade-crown">
                                <FaCrown aria-hidden="true" />
                            </span>
                        </div>
                        <span className="pro-upgrade-eyebrow">BLIFT PRO WORKSPACE</span>
                        <h2>Unlock more of your practice</h2>
                        <p>
                            This feature is part of the <strong>Blift Pro</strong> workspace.
                            Subscribe to unlock Global Pricing, Action Center, Practice Intelligence,
                            Practice Continuity, and more.
                        </p>
                        <div className="pro-upgrade-actions">
                        <button
                            className="pro-upgrade-primary"
                            onClick={() => navigate(LinkFactory.rcic_plans_pricing)}
                        >
                            View Plans &amp; Pricing
                        </button>
                        <button
                            className="pro-upgrade-secondary"
                            onClick={() => navigate(`${LinkFactory.settings_page_rcic}?tab=subscription-billing`)}
                        >
                            Subscribe Now
                        </button>
                        </div>
                </div>
            </MainLayout>
        );
    }

    return <>{children}</>;
};

export default ProRouteGuard;
